import type {
  WebhookDeliveryStatus,
  WebhookDeliverySummary
} from "@gettactic/api";
import type { PaginatedResult } from "@gettactic/api/src/endpoints/response/paginated_result";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTacticHooks } from "../../providers/TacticHooksProvider";

export const WEBHOOK_DELIVERIES_QUERY_KEY = "useWebhookDeliveriesQuery";

const PAGE_SIZE = 20;
// Test events and retries settle on their own; poll so they update in place.
const PENDING_POLL_INTERVAL_MS = 5000;

/** Deliveries of one endpoint, newest first, cursor-paginated. */
export const useWebhookDeliveriesQuery = (
  endpointId: string,
  status: WebhookDeliveryStatus[] = []
) => {
  const { apiClient } = useTacticHooks();

  const query =
    useInfiniteQuery<PaginatedResult<WebhookDeliverySummary> | null>({
      queryKey: [
        WEBHOOK_DELIVERIES_QUERY_KEY,
        apiClient.slug,
        endpointId,
        status
      ],
      queryFn: async ({ pageParam }) =>
        apiClient.orgOrOfficeSettings.listWebhookEndpointDeliveries(
          endpointId,
          {
            cursor: pageParam as string | undefined,
            limit: PAGE_SIZE,
            status
          }
        ),
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => lastPage?.cursor ?? undefined,
      enabled: !!apiClient.slug && !!endpointId,
      refetchInterval: (q) =>
        q.state.data?.pages.some((page) =>
          page?.elements.some((delivery) => delivery.status === "pending")
        )
          ? PENDING_POLL_INTERVAL_MS
          : false
    });

  const deliveries = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page?.elements ?? []),
    [query.data]
  );

  return { ...query, deliveries };
};
